import { cardFeeTaxTotal } from "./receivable.adapter"
import {
  BaseReceivable,
  BillTax,
  ReceivableKind,
  ReceivableKinds,
} from "./receivable.type"
import { Decimal } from "../classes/decimal"
import { Money } from "../classes/money"
import { UUID } from "../classes/uuid"

export const MIN_BILL_PAYMENT = Money.fromCents(50n)

export interface Allocation {
  leID: UUID
  txnID: UUID
  identifier: string
  kind: ReceivableKind
  // positive for charges, negative for credits
  amount: Money
  discount?: Money
}

export type AllocsById = { [key: string]: Allocation }

export function allocationsFor(receivables: BaseReceivable[]): AllocsById {
  return Object.fromEntries(
    receivables.map((receivable) => [
      receivable.ledgerEntryId.toString(),
      {
        leID: receivable.ledgerEntryId,
        txnID: receivable.id,
        identifier: receivable.identifier,
        kind: receivable.kind,
        amount: receivable.adjustedBalance,
        discount: receivable.adjustedBalance.sub(receivable.balance),
      },
    ])
  )
}

export interface AllocationTotals {
  allocationTotal: Money
  allocsByLedgerEntryId: Record<string, Money>
  invoiceTotal: Money
  discountTotal: Money
  financeChargeTotal: Money
  creditTotal: Money
}

export function calculateAllocationTotals(
  allocationsByLedgerEntryId: AllocsById
): AllocationTotals {
  const allocsByLedgerEntryId: Record<string, Money> = {}
  let invoiceTotal = Money.zero()
  let discountTotal = Money.zero()
  let financeChargeTotal = Money.zero()
  let creditTotal = Money.zero()

  for (const id in allocationsByLedgerEntryId) {
    const allocation = allocationsByLedgerEntryId[id]
    allocsByLedgerEntryId[id] = allocation.amount
    switch (allocation.kind) {
      case ReceivableKinds.INVOICE:
        invoiceTotal = invoiceTotal.add(allocation.amount)
        discountTotal = discountTotal.add(allocation.discount)
        break
      case ReceivableKinds.FINANCE_CHARGE:
        financeChargeTotal = financeChargeTotal.add(allocation.amount)
        break
      case ReceivableKinds.CREDIT:
        creditTotal = creditTotal.add(allocation.amount)
        break
    }
  }

  const allocationTotal = invoiceTotal.add(financeChargeTotal).add(creditTotal)

  return {
    allocsByLedgerEntryId,
    invoiceTotal,
    discountTotal,
    financeChargeTotal,
    creditTotal,
    allocationTotal,
  }
}

export interface BillPaymentTotals extends AllocationTotals {
  surcharge: Money
  surchargeRate: Decimal
  surchargeTaxes: BillTax[]
  paymentTotal: Money
}

export function calculateBillPaymentTotals(
  allocationTotals: AllocationTotals,
  isCard: boolean,
  surcharge: Money,
  surchargeTaxes: BillTax[],
  surchargeRate: Decimal
): BillPaymentTotals {
  const allocTotal = allocationTotals.allocationTotal
  if (!isCard) {
    return {
      ...allocationTotals,
      surcharge: Money.zero(),
      surchargeRate: Decimal.zero(),
      surchargeTaxes: [],
      paymentTotal: allocTotal,
    }
  }

  return {
    ...allocationTotals,
    surcharge,
    surchargeRate,
    surchargeTaxes,
    paymentTotal: allocTotal
      .add(surcharge)
      .add(cardFeeTaxTotal(surchargeTaxes)),
  }
}
